<script>
  import { page } from '$app/stores';
  import Button from '$lib/components/Button.svelte';
  import Hemoglobin from '$lib/components/Hemoglobin.svelte';
  import { APP_CONFIG } from '$lib/config';
  import { isSidebarExpanded } from '$lib/features/app/settingsStore';
  import { ROLES } from '$lib/features/auth/authStore';
  import { features } from '$lib/features/feature-flags';
  import {
    useGetNumberOfUnhandledClientsQuery,
    useGetNumberOfUnhandledCompanyMessages,
  } from '$lib/features/messages/services';
  import { twMerge } from 'tailwind-merge';
  import Search from '../Search.svelte';
  import NavItem from './NavItem.svelte';

  let isDevEnv = $state(false);
  if (import.meta.env.VITE_ENVIRONMENT === 'development') {
    isDevEnv = true;
  }

  const unhandledClients = useGetNumberOfUnhandledClientsQuery();
  const unhandledCompanyMessages = useGetNumberOfUnhandledCompanyMessages();
  let animationClass = $state('');

  let expanded = $derived($isSidebarExpanded === 'expanded');

  let sideBarClasses = $derived(
    twMerge([
      'flex flex-col h-full py-2 max-w-56 w-14 ',
      expanded && 'w-56 min-w-56',
      animationClass,
    ]),
  );

  let path = $derived($page.url.pathname);

  let numberOfMessages = $derived(
    $unhandledClients.data?.numberOfNurseUnhandledClientsMessages || 0,
  );
  let numberOfCompanyMessages = $derived(
    $unhandledCompanyMessages.data?.count || 0,
  );

  function toggleSideBar() {
    // We add animation on demand, so that the sidebar doesn't animate/flickering when the page loads.
    if (animationClass === '') {
      animationClass = 'transition-all duration-300 ease-in-out';
    }
    isSidebarExpanded.set(expanded ? 'collapsed' : 'expanded');
  }

  function checkLinkAccess(access = false) {
    return $ROLES.supervisor || access;
  }
</script>

<div
  class="width group sticky top-0 z-20 h-screen bg-gray-1 dark:bg-gray-9"
  style={isDevEnv
    ? `background-image: linear-gradient(45deg, #14140e 25%, #1f1f1f 25%, #1f1f1f 50%, #14140e 50%, #14140e 75%, #1f1f1f 75%, #1f1f1f 100%);
background-size: 56.57px 56.57px;`
    : ''}
>
  <div class={sideBarClasses}>
    {#if expanded}
      <div class="mb-4 flex flex-col px-2">
        <div class="mb-3 mt-2 flex h-8 items-center justify-between gap-2">
          {#if $ROLES.medic || $ROLES.supervisor}
            <div class="flex items-center gap-2">
              <Button variant="link">
                <a href="/">
                  <Hemoglobin size="sm" speed={0.5} />
                </a>
              </Button>
              <div
                class="text-lg text-black transition-colors duration-300 ease-in-out dark:text-white dark:hover:text-gray-1"
              >
                <a href="/">Backoffice</a>
              </div>
            </div>
          {:else}
            <div class="flex items-center gap-2">
              <Hemoglobin size="sm" speed={0.5} />

              <div
                class="text-lg text-black transition-colors duration-300 ease-in-out dark:text-white dark:hover:text-gray-1"
              >
                Backoffice
              </div>
            </div>
          {/if}
          {#if isDevEnv}
            <div
              class="whitespace-nowrap rounded-sm border border-yellow-600 bg-black px-2 py-1 text-sm font-bold text-white"
            >
              👩‍💻👨‍💻 DEV
            </div>
          {/if}
        </div>
        <div class="z-50"><Search /></div>
      </div>
    {:else}
      <div class="mb-6 mt-2 flex h-8 justify-center">
        <a href="/">
          {#if isDevEnv}
            <div
              class="whitespace-nowrap rounded-sm border border-yellow-600 bg-black px-2 py-1 text-sm font-bold text-white"
            >
              👩‍💻
            </div>
          {:else}
            <Hemoglobin size="sm" speed={0.5} />
          {/if}
        </a>
      </div>
    {/if}
    <div class="flex flex-1 flex-col justify-between overflow-y-auto px-2">
      <div class="space-y-1">
        <!-- Do not show for operation support or CSM user-->

        {#if checkLinkAccess($ROLES.medic)}
          <NavItem
            title="Åtgärd behövs"
            active={path === '/'}
            href="/"
            icon="warning"
            {expanded}
          />
        {/if}

        <NavItem
          title="Företag"
          active={path.match(/^\/companies/)}
          href="/companies"
          icon="company"
          {expanded}
        />

        <NavItem
          title="Patienter"
          active={path.match(/^\/clients/)}
          href="/clients"
          icon="group"
          {expanded}
        />

        {#if checkLinkAccess($ROLES.medic)}
          <NavItem
            title="Registrera"
            active={path.match(/^\/register/)}
            href="/register"
            icon="file"
            {expanded}
          />
        {/if}

        {#if checkLinkAccess($ROLES.medic || $ROLES.support)}
          <NavItem
            title="Patientmeddelanden"
            badge={numberOfMessages}
            active={path.match(/^\/cmessages/)}
            href="/messages"
            icon="chat"
            {expanded}
          />
        {/if}

        {#if checkLinkAccess($ROLES.csm || $ROLES.medic || $ROLES.support)}
          <NavItem
            title="Företagsmeddelanden"
            badge={numberOfCompanyMessages}
            active={path.match(/^\/company-messages/)}
            href="/company-messages"
            icon="companyMessage"
            {expanded}
          />
        {/if}

        {#if checkLinkAccess($ROLES.medic || $ROLES.support)}
          <NavItem
            title="Rapporter"
            active={path.match(/^\/reports/)}
            href="/reports/all-answers"
            icon="syringe"
            {expanded}
          />
        {/if}

        <NavItem
          title="Kalender"
          active={path.match(/^\/calendar/)}
          href="/calendar"
          icon="calendar"
          {expanded}
        />

        {#if checkLinkAccess($ROLES.csm)}
          <NavItem
            title="Feedback"
            active={path.match(/^\/feedbacks/)}
            href="/feedbacks"
            icon="benchmark"
            {expanded}
          />
        {/if}

        {#if checkLinkAccess($ROLES.support)}
          <NavItem
            title="Statistik"
            active={path.match(/^\/stats/)}
            href="/stats"
            icon="event_upcoming"
            {expanded}
          />
        {/if}

        {#if checkLinkAccess($ROLES.support || $ROLES.csm)}
          <NavItem
            title="Användare"
            active={path.match(/^\/users/)}
            href="/users"
            icon="addUser"
            {expanded}
          />
        {/if}

        {#if checkLinkAccess($ROLES.csm || $ROLES.medic || $ROLES.support)}
          <NavItem
            title="Org-migrering"
            active={path.match(/^\/org-migration/)}
            href="/org-migration"
            icon="build"
            {expanded}
          />
        {/if}

        {#if $features['messagingLogs']}
          <NavItem
            title="Messages logs"
            active={path.match(/^\/message-logs/)}
            href="/message-logs"
            icon="email"
            {expanded}
          />
        {/if}

        {#if checkLinkAccess($ROLES.supervisor)}
          <NavItem
            title="Interna KPI:er"
            active={path.match(/^\/kpis/)}
            href="/kpis"
            icon="download"
            {expanded}
          />

          {#if $features['knowledge']}
            <NavItem
              title="AI"
              active={path.match(/^\/ai/)}
              href="/ai"
              icon="globe"
              {expanded}
            />
          {/if}

          <NavItem
            title="Feature Toggles"
            active={path.match(/^\/feature-toggle/)}
            href="/feature-toggle"
            icon="settings"
            {expanded}
          />
          {#if $features['okr']}
            <NavItem
              title="OKR"
              active={path.match(/^\/okr/)}
              href="/okr"
              icon="play"
              {expanded}
            />
          {/if}
          {#if $features['testingTools']}
            <NavItem
              title="Testing Tools"
              active={path.match(/^\/testing-tools/)}
              href="/testing-tools"
              icon="build"
              {expanded}
            />
          {/if}
        {/if}

        <NavItem
          title="Hälsoprogram"
          active={path.match(/^\/health-programs/)}
          href="/health-programs"
          icon="human"
          {expanded}
        />

        <NavItem
          title="logs"
          active={path.match(/^\/logs/)}
          href="/logs"
          icon="log"
          {expanded}
        />

        {#if APP_CONFIG.SHOW_DEMO_ACCESS_PAGE}
          <NavItem
            title="Demoåtkomster"
            active={path.match(/^\/demo/)}
            href="/demo"
            icon="play"
            {expanded}
          />
        {/if}
      </div>
      {#if !expanded}
        <button
          type="button"
          class="group/expanded mt-4 flex h-full cursor-pointer flex-col items-center justify-start border-t pt-2 transition-colors duration-300 ease-in-out"
          onclick={toggleSideBar}
        >
          <Button
            iconLeft="arrowForward"
            class="group-hover/expanded:text-white"
            variant="link"
            onclick={toggleSideBar}
          />
        </button>
      {:else}
        <button
          type="button"
          class="group/expanded mt-4 flex h-full cursor-pointer flex-col items-end justify-start border-t pt-2 transition-colors duration-300 ease-in-out"
          onclick={toggleSideBar}
        >
          <Button
            iconLeft="arrowBack"
            class="group-hover/expanded:text-white"
            variant="link"
            onclick={toggleSideBar}
          />
        </button>
      {/if}

      <div class="space-y-1">
        <NavItem
          title="Profil"
          active={path.match(/^\/profile/)}
          href="/profile"
          icon="user"
          {expanded}
        />
        <NavItem
          title="Inställningar"
          active={path.match(/^\/settings/)}
          href="/settings"
          icon="settings"
          {expanded}
        />
        <NavItem href="/logout" title="Logga ut" icon="logout" {expanded} />
      </div>
    </div>
  </div>
</div>
